/**
 * Stage vocabulary + draft validation for the PR engagement controls on
 * `/pr` and `/pr/people/:id`.
 *
 * The pure half of slice `pr-engagement-lifecycle`. Everything here encodes a
 * REAL wire constraint of `record_pr_engagement_event` / `create_pr_engagement`,
 * verified against the actions rather than inferred:
 *
 *  - Stage is FORWARD-ONLY *and skipping is allowed*: `assertStageAdvance`
 *    (`pr-engagement-shared.ts`) rejects only a LOWER rank, and lets `closed`
 *    through from anywhere. That combination is the hazard `stageOptionsAfter`
 *    exists to remove — see its docstring.
 *  - `create_pr_engagement.angle` carries `minLength: 8`.
 *  - `stage: 'closed'` REQUIRES `closed_reason` (an `allOf`/`if`/`then` pair the
 *    schema-form compiler drops, so the modal must enforce it).
 */

/**
 * Declaration order IS pipeline order — mirrors `PR_ENGAGEMENT_STAGES`.
 * 2026-08-15 (inbox-pr-simplify): the FULL order survives here so a legacy row
 * (pre-migration-172 value) still ranks correctly, but the dropdown only
 * OFFERS the settable funnel — see `SETTABLE` below, mirroring the action
 * schema's `PR_SETTABLE_STAGES`.
 */
export const PR_STAGE_ORDER: ReadonlyArray<string> = [
  "pitching",
  "pitched",
  "replied",
  "agreed",
  "prepped",
  "session_done",
  "delivered",
  "closed",
];

/** The stages a write may set — the simplified funnel (the wire contract). */
const SETTABLE: ReadonlySet<string> = new Set([
  "pitched",
  "agreed",
  "delivered",
  "closed",
]);

/**
 * The stages a "Log / advance" control may offer for an engagement currently at
 * `current`: everything from `current` forward, MINUS `closed`.
 *
 * Two separate hazards, both closed here rather than left to the operator:
 *
 *  1. BACKWARDS is a 409 `pr_stage_regression`. Offering it at all means the
 *     UI's own dropdown produces a guaranteed error.
 *  2. SKIPPING is *allowed by the action* and is the worse of the two. Stamps
 *     are first-wins and the machine is forward-only, so jumping
 *     `pitching → delivered` stamps `delivered_at` and leaves `pitched_at` /
 *     `replied_at` / `agreed_at` NULL FOREVER. The `/pr` overview counters are
 *     stamp-derived, so that collaboration silently vanishes from "contacted"
 *     and "replied" — and `reconcile_pr_board`'s `stage_stamp_missing` only
 *     checks the stamp for the CURRENT stage, which is set, so nothing ever
 *     flags it.
 *
 * Skipping is therefore still POSSIBLE (the action allows it, and the CLI can
 * do it deliberately), just not one careless click away on a board.
 *
 * `closed` is excluded because closing requires a `closed_reason` and gets its
 * own control; a stage dropdown cannot express that conditional requirement.
 */
export function stageOptionsAfter(current: string): ReadonlyArray<string> {
  const rank = PR_STAGE_ORDER.indexOf(current);
  if (rank === -1) return [];
  // Settable-only: `record_pr_engagement_event` rejects the legacy
  // intermediate stages since inbox-pr-simplify, so offering them would make
  // the UI's own dropdown produce a guaranteed input_invalid. `pitching`
  // never appears either — it is create-only (rank 0, below every settable).
  return PR_STAGE_ORDER.filter(
    (s, i) => i >= rank && s !== "closed" && SETTABLE.has(s),
  );
}

/**
 * Event kinds the "Log / advance" control offers.
 *
 * Excludes `closed` — a `kind: "closed"` with no `stage` logs a closure that
 * never happened, and closing has its own control that collects the reason.
 */
export const PR_LOG_EVENT_KINDS: ReadonlyArray<string> = [
  "created",
  "researched",
  "pitch_staged",
  "pitch_sent",
  "reply",
  "reply_sent",
  "agreed",
  "prepped",
  "session",
  "followed_up",
  "delivered",
  "note",
];

/** `exchange_kind` values, with labels an operator would recognise. */
export const PR_EXCHANGE_KIND_OPTIONS: ReadonlyArray<{
  readonly value: string;
  readonly label: string;
}> = [
  { value: "publication", label: "publication — they write about us" },
  { value: "social_content", label: "social content — they post" },
  { value: "paying_plus_one", label: "paying plus-one — they bring someone" },
  { value: "partnership", label: "partnership — an ongoing arrangement" },
];

export const PR_SESSION_LOCATION_OPTIONS: ReadonlyArray<{
  readonly value: string;
  readonly label: string;
}> = [
  { value: "studio", label: "studio" },
  { value: "home", label: "home" },
];

/** `create_pr_engagement.angle` minLength. */
export const PR_ANGLE_MIN_LENGTH = 8;

/**
 * Cross-field rule for `record_pr_engagement_event`: `stage: 'closed'` requires
 * `closed_reason`. Returns `null` when the draft is submittable.
 */
export function validatePrEventDraft(
  values: Readonly<Record<string, string | boolean>>,
): string | null {
  const stage = typeof values["stage"] === "string" ? values["stage"] : "";
  const reason =
    typeof values["closed_reason"] === "string" ? values["closed_reason"] : "";
  if (stage === "closed" && reason.trim() === "") {
    return "Closing a collaboration needs a reason — say how it ended.";
  }
  return null;
}

/**
 * `create_pr_engagement.angle` is `minLength: 8`, so an operator typing "PR"
 * would otherwise meet a raw Ajv `input_invalid`.
 */
export function validateCreateEngagementDraft(
  values: Readonly<Record<string, string | boolean>>,
): string | null {
  const angle = typeof values["angle"] === "string" ? values["angle"] : "";
  if (angle.trim().length < PR_ANGLE_MIN_LENGTH) {
    return `Say what the angle is — at least ${PR_ANGLE_MIN_LENGTH} characters.`;
  }
  return null;
}
